import React, { useEffect, useRef, useState } from "react";
import { Table, Button, Form, Spinner, InputGroup, Badge, Tabs, Tab } from "react-bootstrap";
import { useAuth } from "../../auth/AuthContext";
import * as familiaGastosApi from "../../api/familiaGastos";
import * as reportesApi from "../../api/reportes";
import { listarCuentas } from "../../api/cuentas";
import { listarDeudores } from "../../api/cobrosFamilia";
import ModalConfirmar from "../../components/ModalConfirmar";
import ModalAjusteFamiliar from "./ModalAjusteFamiliar";
import GastosComunesTab from "./GastosComunesTab";
import { formatoNumero } from "../../utils/moneda";

const ANCHO_COL_GASTOS = 170;
const ANCHO_COL_MONTO = 100;
const ANCHO_COL_MIEMBRO = 130;

/** Vista compartida de la familia, estilo Excel, en dos pestañas: "Proyección" (filas = gastos
 * comunes, columnas = Monto + una por miembro; cada quien edita solo su propia columna, ve las
 * demás en solo lectura) y "Ajustes" (filas libres tipo Manual o Fórmula). Puramente informativo
 * (no mueve saldos de cuentas). Los cambios de la grilla se acumulan localmente mientras se editan
 * (sin guardar en cada blur, para no perder la posición) y se confirman todos juntos con "Guardar".
 *
 * La fila de items scrollea verticalmente en su propio contenedor de altura acotada; los
 * totales (TOTAL GASTOS / Sueldo / NETO) van en una tabla aparte, fuera de ese scroll, así que
 * siempre quedan visibles sin depender de "position: sticky" en <tfoot> (que en la práctica se
 * superpone entre navegadores cuando hay varias filas). Las columnas de ambas tablas se
 * mantienen alineadas con anchos fijos idénticos (colgroup) y el scroll horizontal se sincroniza
 * entre las dos para que también se vea bien en móvil con varios miembros. */
export default function FamiliaGastosPage() {
  const { usuario, monedaPredeterminada } = useAuth();
  const [grid, setGrid] = useState(null);
  const [tarjetas, setTarjetas] = useState([]);
  const [deudores, setDeudores] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [cambios, setCambios] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [showModalAjuste, setShowModalAjuste] = useState(false);
  const [ajusteEditando, setAjusteEditando] = useState(null);
  const [aEliminarAjuste, setAEliminarAjuste] = useState(null);
  const [errorEliminarAjuste, setErrorEliminarAjuste] = useState("");
  const [tab, setTab] = useState("proyeccion");
  const [modoReordenarAjustes, setModoReordenarAjustes] = useState(false);
  const [ordenTrabajoAjustes, setOrdenTrabajoAjustes] = useState([]);
  const [guardandoOrdenAjustes, setGuardandoOrdenAjustes] = useState(false);
  const scrollCuerpoRef = useRef(null);
  const scrollPieRef = useRef(null);
  const sincronizando = useRef(false);

  const cargar = async () => {
    setCargando(true);
    try {
      setGrid(await familiaGastosApi.obtenerGrid());
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargar();
    listarCuentas().then((cuentas) => setTarjetas(cuentas.filter((c) => c.tipo === "TarjetaCredito" && c.activo)));
    listarDeudores().then(setDeudores);
  }, []);

  // Cada cambio pendiente se identifica por gasto+miembro (o sueldo+miembro) -- antes solo hacía
  // falta el gastoComunId porque cada quien únicamente podía tocar su propia columna, pero ahora el
  // Jefe puede tener varios cambios pendientes de distintos miembros a la vez.
  const marcarAporte = (gastoComunId, monto, usuarioId) =>
    setCambios((prev) => ({ ...prev, [`aporte-${gastoComunId}-${usuarioId}`]: { tipo: "aporte", gastoComunId, monto, usuarioId } }));

  const marcarSueldo = (monto, usuarioId) =>
    setCambios((prev) => ({ ...prev, [`sueldo-${usuarioId}`]: { tipo: "sueldo", monto, usuarioId } }));

  const hayCambios = Object.keys(cambios).length > 0;

  const guardarCambios = async () => {
    setGuardando(true);
    try {
      for (const c of Object.values(cambios)) {
        if (c.tipo === "aporte") await familiaGastosApi.registrarAporte(c.gastoComunId, Number(c.monto || 0), c.usuarioId);
        else await familiaGastosApi.registrarSueldo(Number(c.monto || 0), c.usuarioId);
      }
      setCambios({});
      await cargar();
    } finally {
      setGuardando(false);
    }
  };

  const abrirReordenarAjustes = () => {
    setOrdenTrabajoAjustes([...grid.ajustes]);
    setModoReordenarAjustes(true);
  };

  const moverFilaAjuste = (idx, delta) => {
    setOrdenTrabajoAjustes((prev) => {
      const destino = idx + delta;
      if (destino < 0 || destino >= prev.length) return prev;
      const nuevo = [...prev];
      [nuevo[idx], nuevo[destino]] = [nuevo[destino], nuevo[idx]];
      return nuevo;
    });
  };

  const guardarOrdenAjustes = async () => {
    setGuardandoOrdenAjustes(true);
    try {
      await familiaGastosApi.guardarOrdenAjustes(ordenTrabajoAjustes.map((a) => a.ajusteId));
      setModoReordenarAjustes(false);
      await cargar();
    } finally {
      setGuardandoOrdenAjustes(false);
    }
  };

  const sincronizarScroll = (origen, destino) => () => {
    if (sincronizando.current || !destino.current) return;
    sincronizando.current = true;
    destino.current.scrollLeft = origen.current.scrollLeft;
    sincronizando.current = false;
  };

  if (cargando || !grid) return <Spinner animation="border" />;

  if (!grid.miembros.length) {
    return <p className="text-muted">Aún no hay miembros de familia registrados como Cliente.</p>;
  }

  // La columna del usuario logueado va primero; el resto conserva el orden que manda el servidor
  // (Usuarios.OrdenFamilia, y por nombre si empatan). Array.sort es estable, así que devolver 0 no los reordena.
  const miembros = [...grid.miembros].sort((a, b) => {
    if (a.email === usuario.email) return -1;
    if (b.email === usuario.email) return 1;
    return 0;
  });

  const aportesPorMiembro = {};
  miembros.forEach((m) => {
    aportesPorMiembro[m.usuarioId] = Object.fromEntries(m.aportes.map((a) => [a.gastoComunId, a.monto]));
  });

  const formatoMoneda = (v) => Number(v).toLocaleString("es-ES", { style: "currency", currency: monedaPredeterminada.codigo });
  const totalMontoPrincipal = grid.items.reduce((acc, i) => acc + Number(i.montoPrincipal), 0);
  // La lista de ajustes SIEMPRE muestra todas las filas (aunque el valor calculado dé $0, negativo o
  // positivo) -- un ajuste en $0 sigue siendo información real (ej. "ya liquidé todo lo de la tarjeta
  // este mes"), no un dato vacío para esconder. Este filtro solo se usa para el selector de "elegir
  // ajuste a encadenar" del modal (ajustesDisponibles), donde sí conviene no ofrecer ajustes en $0.
  const ajustesConValor = grid.ajustes.filter((a) => Number(a.valor) !== 0);

  const columnas = (
    <colgroup>
      <col style={{ width: ANCHO_COL_GASTOS }} />
      <col style={{ width: ANCHO_COL_MONTO }} />
      {miembros.map((m) => <col key={m.usuarioId} style={{ width: ANCHO_COL_MIEMBRO }} />)}
    </colgroup>
  );

  return (
    <div>
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
        <h4 className="mb-0">Proyección de gastos Familiares</h4>
        <Button size="sm" variant="primary" onClick={() => reportesApi.descargarFamiliaGastosPdf()}>
          <i className="bi bi-file-earmark-pdf me-1"></i>Descargar PDF
        </Button>
      </div>

      <Tabs activeKey={tab} onSelect={(k) => setTab(k)} className="mb-3">
        <Tab eventKey="proyeccion" title="Proyección">
          <div className="d-flex justify-content-end mb-2 mt-3">
            <Button size="sm" variant="success" onClick={guardarCambios} disabled={!hayCambios || guardando}>
              {guardando ? <Spinner size="sm" animation="border" /> : <><i className="bi bi-floppy-fill me-1"></i>Guardar</>}
            </Button>
          </div>

          <div className="fs-card p-0 overflow-hidden">
            <div
              className="table-responsive fs-table-scroll"
              ref={scrollCuerpoRef}
              onScroll={sincronizarScroll(scrollCuerpoRef, scrollPieRef)}
            >
              <Table hover className="table-fs table-fs-fixed mb-0 align-middle">
                {columnas}
                <thead>
                  <tr>
                    <th>Gastos</th>
                    <th className="text-end">Monto</th>
                    {miembros.map((m) => (
                      <th key={m.usuarioId} className="text-end">{m.nombres}{m.email === usuario.email ? " (yo)" : ""}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {grid.items.map((item) => (
                    <tr key={item.gastoComunId}>
                      <td>{item.nombre}</td>
                      <td className="text-end">${formatoNumero(item.montoPrincipal)}</td>
                      {miembros.map((m) => (
                        <CeldaAporte
                          key={m.usuarioId}
                          montoActual={aportesPorMiembro[m.usuarioId][item.gastoComunId] || 0}
                          valorPendiente={cambios[`aporte-${item.gastoComunId}-${m.usuarioId}`]?.monto}
                          editable={m.email === usuario.email}
                          puedeEditarOtros={grid.esJefe}
                          onCambiar={(monto) => marcarAporte(item.gastoComunId, monto, m.usuarioId)}
                        />
                      ))}
                    </tr>
                  ))}
                  {!grid.items.length && (
                    <tr><td colSpan={2 + miembros.length} className="text-center text-muted py-4">No hay gastos comunes registrados.</td></tr>
                  )}
                </tbody>
              </Table>
            </div>

            <div
              className="table-responsive fs-table-foot"
              ref={scrollPieRef}
              onScroll={sincronizarScroll(scrollPieRef, scrollCuerpoRef)}
            >
              <Table hover className="table-fs table-fs-fixed mb-0 align-middle">
                {columnas}
                <tbody>
                  <tr className="fw-bold">
                    <td>TOTAL GASTOS</td>
                    <td className="text-end">${formatoNumero(totalMontoPrincipal)}</td>
                    {miembros.map((m) => (
                      <td key={m.usuarioId} className="text-end text-fs-gasto">{formatoMoneda(m.totalGastos)}</td>
                    ))}
                  </tr>
                  <tr>
                    <td colSpan={2}>Sueldo</td>
                    {miembros.map((m) => (
                      <CeldaSueldo
                        key={m.usuarioId}
                        montoActual={m.sueldo}
                        valorPendiente={cambios[`sueldo-${m.usuarioId}`]?.monto}
                        editable={m.email === usuario.email}
                        puedeEditarOtros={grid.esJefe}
                        onCambiar={(monto) => marcarSueldo(monto, m.usuarioId)}
                      />
                    ))}
                  </tr>
                  <tr className="fw-bold">
                    <td colSpan={2}>NETO</td>
                    {miembros.map((m) => (
                      <td key={m.usuarioId} className={`text-end ${m.neto >= 0 ? "text-fs-ingreso" : "text-fs-gasto"}`}>{formatoMoneda(m.neto)}</td>
                    ))}
                  </tr>
                </tbody>
              </Table>
            </div>
          </div>

          {hayCambios && (
            <p className="text-muted small mt-2 mb-0">
              <i className="bi bi-info-circle me-1"></i>Tienes cambios sin guardar. Pulsa "Guardar" para confirmarlos.
            </p>
          )}
        </Tab>

        {grid.esJefe && (
          <Tab eventKey="gastos-comunes" title="Gastos Comunes">
            <GastosComunesTab items={grid.items} onCambio={cargar} />
          </Tab>
        )}

        <Tab eventKey="ajustes" title="Ajustes">
          <div className="d-flex justify-content-end gap-2 mb-2 mt-3">
            {grid.ajustes.length > 1 && (
              <Button size="sm" variant="outline-secondary" onClick={abrirReordenarAjustes}>
                <i className="bi bi-arrow-down-up me-1"></i>Reordenar
              </Button>
            )}
            <Button size="sm" variant="outline-primary" onClick={() => { setAjusteEditando(null); setShowModalAjuste(true); }}>
              <i className="bi bi-plus-lg me-1"></i>Nuevo ajuste
            </Button>
          </div>

          {modoReordenarAjustes ? (
            <div className="fs-card p-0 overflow-hidden">
              <div className="p-3 pb-2 text-muted small">
                <i className="bi bi-info-circle me-1"></i>
                Usa las flechas para ordenar tus ajustes.
              </div>
              <div className="list-group list-group-flush">
                {ordenTrabajoAjustes.map((a, idx) => (
                  <div key={a.ajusteId} className="list-group-item d-flex align-items-center gap-3 py-3">
                    <div className="flex-grow-1">
                      <div className="fw-semibold">{a.descripcion}</div>
                      <div className="text-muted small">
                        <Badge bg={a.tipo === "Formula" ? "info" : "secondary"}>{a.tipo === "Formula" ? "Fórmula" : "Manual"}</Badge>
                        <span className="ms-2">{formatoMoneda(a.valor)}</span>
                      </div>
                    </div>
                    <div className="d-flex gap-1">
                      <Button size="sm" variant="outline-secondary" disabled={idx === 0}
                        onClick={() => moverFilaAjuste(idx, -1)} aria-label="Subir">
                        <i className="bi bi-arrow-up"></i>
                      </Button>
                      <Button size="sm" variant="outline-secondary" disabled={idx === ordenTrabajoAjustes.length - 1}
                        onClick={() => moverFilaAjuste(idx, 1)} aria-label="Bajar">
                        <i className="bi bi-arrow-down"></i>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="d-flex justify-content-end gap-2 p-3">
                <Button variant="outline-secondary" onClick={() => setModoReordenarAjustes(false)} disabled={guardandoOrdenAjustes}>Cancelar</Button>
                <Button variant="primary" onClick={guardarOrdenAjustes} disabled={guardandoOrdenAjustes}>
                  {guardandoOrdenAjustes ? <Spinner size="sm" animation="border" /> : "Guardar orden"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="fs-card p-0 overflow-hidden">
              <Table responsive hover className="table-fs mb-0 align-middle">
                <thead>
                  <tr>
                    <th>Descripción</th><th>Tipo</th><th className="text-end">Valor</th><th className="text-end">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {grid.ajustes.map((a) => (
                    <tr key={a.ajusteId}>
                      <td>{a.descripcion}</td>
                      <td><Badge bg={a.tipo === "Formula" ? "info" : "secondary"}>{a.tipo === "Formula" ? "Fórmula" : "Manual"}</Badge></td>
                      <td className={`text-end fw-semibold ${a.valor >= 0 ? "text-fs-ingreso" : "text-fs-gasto"}`}>{formatoMoneda(a.valor)}</td>
                      <td className="text-end">
                        <Button size="sm" variant="outline-primary" className="me-1" onClick={() => { setAjusteEditando(a); setShowModalAjuste(true); }}>
                          <i className="bi bi-pencil"></i>
                        </Button>
                        <Button size="sm" variant="outline-danger" onClick={() => { setErrorEliminarAjuste(""); setAEliminarAjuste(a); }}>
                          <i className="bi bi-trash"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {!grid.ajustes.length && (
                    <tr><td colSpan={4} className="text-center text-muted py-4">Sin ajustes personalizados todavía.</td></tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Tab>
      </Tabs>

      <ModalAjusteFamiliar
        show={showModalAjuste}
        onHide={() => setShowModalAjuste(false)}
        ajuste={ajusteEditando}
        miembros={miembros}
        gastosComunes={grid.items}
        tarjetas={tarjetas}
        deudores={deudores}
        ajustesDisponibles={ajustesConValor}
        onGuardado={() => { setShowModalAjuste(false); cargar(); }}
      />
      <ModalConfirmar
        show={!!aEliminarAjuste}
        titulo="Eliminar ajuste"
        mensaje={`¿Eliminar el ajuste "${aEliminarAjuste?.descripcion}"?`}
        error={errorEliminarAjuste}
        onConfirmar={async () => {
          try {
            await familiaGastosApi.eliminarAjuste(aEliminarAjuste.ajusteId);
            setAEliminarAjuste(null);
            cargar();
          } catch (err) {
            setErrorEliminarAjuste(err?.response?.data?.mensaje || "No se pudo eliminar.");
          }
        }}
        onCancelar={() => setAEliminarAjuste(null)}
      />
    </div>
  );
}

function CeldaAporte({ montoActual, valorPendiente, editable, puedeEditarOtros, onCambiar }) {
  const valor = valorPendiente !== undefined ? valorPendiente : montoActual;

  if (editable) return <CeldaInput valor={valor} onCambiar={onCambiar} autoFocus={false} />;
  if (!puedeEditarOtros) return <td className="text-end">${formatoNumero(montoActual)}</td>;

  return (
    <CeldaClicParaEditar
      valor={valor}
      onCambiar={onCambiar}
      formatear={(v) => `$${formatoNumero(v)}`}
    />
  );
}

function CeldaSueldo({ montoActual, valorPendiente, editable, puedeEditarOtros, onCambiar }) {
  const { monedaPredeterminada } = useAuth();
  const valor = valorPendiente !== undefined ? valorPendiente : montoActual;
  const formatear = (v) => Number(v).toLocaleString("es-ES", { style: "currency", currency: monedaPredeterminada.codigo });

  if (editable) return <CeldaInput valor={valor} onCambiar={onCambiar} autoFocus={false} />;
  if (!puedeEditarOtros) return <td className="text-end">{formatear(montoActual)}</td>;

  return <CeldaClicParaEditar valor={valor} onCambiar={onCambiar} formatear={formatear} />;
}

/** Celda siempre editable (la columna del propio usuario, igual que siempre). */
function CeldaInput({ valor, onCambiar, autoFocus }) {
  return (
    <td className="text-end">
      <InputGroup size="sm" style={{ maxWidth: 130, marginLeft: "auto" }}>
        <InputGroup.Text>$</InputGroup.Text>
        <Form.Control type="number" step="0.01" min="0" autoFocus={autoFocus} value={valor} onChange={(e) => onCambiar(e.target.value)} />
      </InputGroup>
    </td>
  );
}

/** Celda de OTRO miembro que el Jefe puede editar: en reposo se ve como texto plano (el lápiz solo
 * aparece al pasar el mouse/enfocar); un clic la convierte en el mismo input de la columna propia.
 * Vuelve a texto al perder el foco o al presionar Enter -- nunca se ven varios inputs juntos salvo
 * que se estén usando de verdad. */
function CeldaClicParaEditar({ valor, onCambiar, formatear }) {
  const [editando, setEditando] = useState(false);

  if (editando) {
    return (
      <td className="text-end">
        <InputGroup size="sm" style={{ maxWidth: 130, marginLeft: "auto" }}>
          <InputGroup.Text>$</InputGroup.Text>
          <Form.Control
            type="number" step="0.01" min="0" autoFocus
            value={valor}
            onChange={(e) => onCambiar(e.target.value)}
            onBlur={() => setEditando(false)}
            onKeyDown={(e) => { if (e.key === "Enter") setEditando(false); }}
          />
        </InputGroup>
      </td>
    );
  }

  return (
    <td className="text-end">
      <button type="button" className="fs-celda-clic-editar" onClick={() => setEditando(true)} title="Clic para editar">
        <i className="bi bi-pencil fs-celda-clic-editar-icon me-1" aria-hidden="true"></i>{formatear(valor)}
      </button>
    </td>
  );
}
